import React from 'react'
import classNames from 'classnames/bind'
import type { PlanLimitsInfo, PlanUsageInfo } from '../../types'
import styles from '../../UserProfile.module.scss'

const cx = classNames.bind(styles)

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const value = bytes / Math.pow(1024, i)
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function usagePercent(used: number, limit: number): number {
  if (limit === 0) return 0
  return Math.min(100, Math.round((used / limit) * 100))
}

interface PlanLimitsTabProps {
  limits: PlanLimitsInfo
  usage: PlanUsageInfo
}

interface UsageRowProps {
  label: string
  used: number
  limit: number
}

function UsageRow({ label, used, limit }: UsageRowProps) {
  const percent = usagePercent(used, limit)
  const isWarning = percent >= 80
  const isDanger = percent >= 95

  return (
    <div className={cx('usageRow')}>
      <div className={cx('usageHeader')}>
        <span className={cx('usageLabel')}>{label}</span>
        <span className={cx('usageValue')}>
          {formatBytes(used)} / {formatBytes(limit)}
        </span>
      </div>
      <div className={cx('usageBar')}>
        <div
          className={cx('usageBarFill', {
            usageBarFillWarning: isWarning && !isDanger,
            usageBarFillDanger: isDanger,
          })}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className={cx('usagePercent', {
        usagePercentWarning: isWarning && !isDanger,
        usagePercentDanger: isDanger,
      })}>
        {percent}% used
      </div>
    </div>
  )
}

export default function PlanLimitsTab({ limits, usage }: PlanLimitsTabProps) {
  return (
    <div className={cx('planLimits')}>
      <div className={cx('planLimitsSection')}>
        <h3 className={cx('planLimitsSectionTitle')}>Current Usage</h3>
        <UsageRow
          label="Text storage"
          used={usage.text_storage_bytes}
          limit={limits.max_text_storage_bytes}
        />
        <UsageRow
          label="File storage"
          used={usage.file_storage_bytes}
          limit={limits.max_file_storage_bytes}
        />
      </div>

      <div className={cx('planLimitsSection')}>
        <h3 className={cx('planLimitsSectionTitle')}>Plan Limits</h3>
        <ul className={cx('limitsList')}>
          <li className={cx('limitsItem')}>
            <span className={cx('limitsLabel')}>Max note size</span>
            <span className={cx('limitsValue')}>{formatBytes(limits.max_note_size_bytes)}</span>
          </li>
          <li className={cx('limitsItem')}>
            <span className={cx('limitsLabel')}>Max text storage</span>
            <span className={cx('limitsValue')}>{formatBytes(limits.max_text_storage_bytes)}</span>
          </li>
          <li className={cx('limitsItem')}>
            <span className={cx('limitsLabel')}>Max file storage</span>
            <span className={cx('limitsValue')}>{formatBytes(limits.max_file_storage_bytes)}</span>
          </li>
          <li className={cx('limitsItem')}>
            <span className={cx('limitsLabel')}>Max versions per note</span>
            <span className={cx('limitsValue')}>{limits.max_versions_per_note}</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

